/**
 * AssignQRCode Page
 * 
 * Allows users to scan a pre-printed QR code and either:
 * 1. Assign it to an existing box
 * 2. Create a new box with that QR code
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddBoxIcon from '@mui/icons-material/AddBox';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useAppNavigation } from '../hooks/useAppNavigation';
import QRScanner from '../components/QRScanner';
import api from '../services/api';
import { colors, shadows } from '../styles/styleConstants';
import ROUTES from '../config/routes';

const steps = ['Scan QR Code', 'Choose Action', 'Complete'];

const AssignQRCode = () => {
  const navigate = useAppNavigation();
  const [activeStep, setActiveStep] = useState(0);
  const [scannedUuid, setScannedUuid] = useState(null);
  const [uuidStatus, setUuidStatus] = useState(null); // 'available', 'owned', 'taken'
  const [existingBox, setExistingBox] = useState(null);
  const [userBoxes, setUserBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state for creating new box
  const [newBoxName, setNewBoxName] = useState('');
  const [newBoxDescription, setNewBoxDescription] = useState('');
  const [newBoxLocation, setNewBoxLocation] = useState('');
  
  // Form state for assigning to existing box
  const [selectedBoxId, setSelectedBoxId] = useState('');
  
  // Dialog for manual UUID entry
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualUuid, setManualUuid] = useState('');

  // Fetch user's boxes when component mounts
  useEffect(() => {
    fetchUserBoxes();
  }, []);

  const fetchUserBoxes = async () => {
    try {
      const response = await api.get('/boxes');
      setUserBoxes(response.data || []);
    } catch (err) {
      console.error('Error fetching boxes:', err);
    }
  };

  const handleScan = async ({ uuid }) => {
    setScannedUuid(uuid);
    setError(null);
    await checkUuidStatus(uuid);
  };

  const checkUuidStatus = async (uuid) => {
    setLoading(true);
    try {
      const response = await api.get(`/boxes/uuid/${uuid}/check`);
      setUuidStatus(response.data.status);
      
      if (response.data.status === 'owned') {
        setExistingBox(response.data.box);
      }
      
      setActiveStep(1);
    } catch (err) {
      console.error('Error checking UUID:', err);
      setError('Failed to check QR code status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async () => {
    const uuid = manualUuid.trim().toLowerCase();
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (!uuidPattern.test(uuid)) {
      setError('Invalid UUID format. Please enter a valid UUID.');
      return;
    }
    
    setManualEntryOpen(false);
    setScannedUuid(uuid);
    await checkUuidStatus(uuid);
  };

  const handleAssignToExistingBox = async () => {
    if (!selectedBoxId) {
      setError('Please select a box');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await api.post(`/boxes/${selectedBoxId}/assign-uuid`, { uuid: scannedUuid });
      setSuccess('QR code successfully assigned to the box!');
      setActiveStep(2);
    } catch (err) {
      console.error('Error assigning UUID:', err);
      if (err.response?.status === 409) {
        setError('This QR code is already assigned to another box.');
      } else {
        setError(err.response?.data?.error || 'Failed to assign QR code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewBox = async () => {
    if (!newBoxName.trim()) {
      setError('Please enter a box name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/boxes/create-with-uuid', {
        uuid: scannedUuid,
        name: newBoxName.trim(),
        description: newBoxDescription.trim() || null,
        location: newBoxLocation.trim() || null
      });
      
      setExistingBox(response.data);
      setSuccess('New box created successfully with the QR code!');
      setActiveStep(2);
    } catch (err) {
      console.error('Error creating box:', err);
      if (err.response?.status === 409) {
        setError('This QR code is already assigned to a box.');
      } else {
        setError(err.response?.data?.error || 'Failed to create box. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewBox = () => {
    if (existingBox) {
      navigate.to.BOX_DETAIL(existingBox.id);
    }
  };

  const handleStartOver = () => {
    setActiveStep(0);
    setScannedUuid(null);
    setUuidStatus(null);
    setExistingBox(null);
    setError(null);
    setSuccess(null);
    setNewBoxName('');
    setNewBoxDescription('');
    setNewBoxLocation('');
    setSelectedBoxId('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <QRScanner
              onScan={handleScan}
              onError={(err) => setError(err.message)}
            />
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">OR</Typography>
            </Divider>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setManualEntryOpen(true)}
              sx={{ py: 1.5 }}
            >
              Enter UUID Manually
            </Button>
          </Box>
        );
      
      case 1:
        if (loading) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Checking QR code...</Typography>
            </Box>
          );
        }
        
        if (uuidStatus === 'taken') {
          return (
            <Alert severity="error" sx={{ mb: 2 }}>
              This QR code is already assigned to another user's box. You cannot use it.
              <Button onClick={handleStartOver} sx={{ mt: 1 }} fullWidth variant="outlined">
                Scan Another Code
              </Button>
            </Alert>
          );
        }
        
        if (uuidStatus === 'owned') {
          return (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                This QR code is already assigned to your box: <strong>{existingBox?.name}</strong>
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={handleViewBox}
                startIcon={<InventoryIcon />}
                sx={{
                  backgroundColor: colors.actionBlue,
                  mb: 2,
                  '&:hover': { backgroundColor: colors.actionBlue }
                }}
              >
                View Box
              </Button>
              <Button variant="outlined" fullWidth onClick={handleStartOver}>
                Scan Another Code
              </Button>
            </Box>
          );
        }
        
        // UUID is available - show options
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              This QR code is available! Choose what to do with it:
            </Alert>
            
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Scanned UUID:
            </Typography>
            <Paper sx={{ p: 1.5, mb: 3, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
              {scannedUuid}
            </Paper>
            
            {/* Option 1: Assign to existing box */}
            <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LinkIcon sx={{ mr: 1, color: colors.actionBlue }} />
                  <Typography variant="h6">Assign to Existing Box</Typography>
                </Box>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select a Box</InputLabel>
                  <Select
                    value={selectedBoxId}
                    onChange={(e) => setSelectedBoxId(e.target.value)}
                    label="Select a Box"
                  >
                    {userBoxes.map((box) => (
                      <MenuItem key={box.id} value={box.id}>
                        {box.name} {box.location ? `(${box.location})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAssignToExistingBox}
                  disabled={!selectedBoxId || loading}
                  sx={{
                    backgroundColor: colors.actionBlue,
                    '&:hover': { backgroundColor: colors.actionBlue }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Assign QR Code'}
                </Button>
              </CardContent>
            </Card>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">OR</Typography>
            </Divider>
            
            {/* Option 2: Create new box */}
            <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AddBoxIcon sx={{ mr: 1, color: colors.actionBlue }} />
                  <Typography variant="h6">Create New Box</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Box Name"
                  value={newBoxName}
                  onChange={(e) => setNewBoxName(e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={newBoxDescription}
                  onChange={(e) => setNewBoxDescription(e.target.value)}
                  sx={{ mb: 2 }}
                  multiline
                  rows={2}
                />
                
                <TextField
                  fullWidth
                  label="Location (optional)"
                  value={newBoxLocation}
                  onChange={(e) => setNewBoxLocation(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="e.g., Garage - Shelf 1"
                />
                
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreateNewBox}
                  disabled={!newBoxName.trim() || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AddBoxIcon />}
                  sx={{
                    backgroundColor: colors.actionBlue,
                    '&:hover': { backgroundColor: colors.actionBlue }
                  }}
                >
                  Create Box with QR Code
                </Button>
              </CardContent>
            </Card>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Success!
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
            
            {existingBox && (
              <Button
                variant="contained"
                onClick={handleViewBox}
                startIcon={<InventoryIcon />}
                sx={{
                  backgroundColor: colors.actionBlue,
                  mb: 2,
                  mr: 1,
                  '&:hover': { backgroundColor: colors.actionBlue }
                }}
              >
                View Box
              </Button>
            )}
            
            <Button variant="outlined" onClick={handleStartOver}>
              Scan Another Code
            </Button>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      <Container maxWidth="sm">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate.to.APP_HOME()}
          sx={{ mb: 2 }}
        >
          Back to Home
        </Button>
        
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: shadows.lg }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <QrCodeScannerIcon sx={{ fontSize: 32, mr: 1, color: colors.actionBlue }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
              Assign QR Code to Box
            </Typography>
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {renderStepContent()}
        </Paper>
      </Container>
      
      {/* Manual UUID Entry Dialog */}
      <Dialog open={manualEntryOpen} onClose={() => setManualEntryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter UUID from ClutterLogix Label</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the UUID from your ClutterLogix QR code label. The UUID is printed below the QR code in this format:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2, color: 'text.secondary' }}>
            xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
          </Typography>
          <TextField
            fullWidth
            label="UUID from ClutterLogix Label"
            value={manualUuid}
            onChange={(e) => setManualUuid(e.target.value)}
            placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
            helperText="Only UUIDs from ClutterLogix QR codes are accepted"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualEntryOpen(false)}>Cancel</Button>
          <Button onClick={handleManualEntry} variant="contained">
            Check UUID
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignQRCode;
